import React, { useState, useEffect, useCallback } from 'react';
import { payoutAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Upload, Search, Filter, ChevronLeft, ChevronRight,
  DollarSign, FileSpreadsheet, Loader2, Pencil, Trash2, X, Download,
  CheckCircle2, Clock, CreditCard,
} from 'lucide-react';

// Updated entry types as requested
const ENTRY_TYPES = ['Commission', 'Override', 'Reimbursement', 'Chargeback', 'Adjustment', 'Marketing Support'];
const PAYEE_TYPES = ['Agent', 'Agency'];
const PAYMENT_STATUSES = ['Scheduled', 'Paid'];
const PAYMENT_METHODS = ['Direct Deposit', 'Check'];

const STATUS_COLORS = {
  Scheduled: 'bg-amber-100 text-amber-700',
  Paid: 'bg-emerald-100 text-emerald-700',
};

const STATUS_ICONS = {
  Scheduled: <Clock size={12} />,
  Paid: <CheckCircle2 size={12} />,
};

function LedgerEntryForm({ onSave, onClose, initial }) {
  const [form, setForm] = useState(initial || {
    payee_type: 'Agent', payee_name: '', payee_id: '', agency_name: '', agent_name: '',
    client_name: '', product: '', carrier: '', effective_date: '',
    entry_type: 'Commission', amount: '', notes: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_status: 'Scheduled',
    payment_method: 'Direct Deposit',
    reference_number: '',
  });
  const [saving, setSaving] = useState(false);
  const [payeeSearch, setPayeeSearch] = useState(initial?.payee_name || '');
  const [payeeResults, setPayeeResults] = useState([]);
  const [payeeLoading, setPayeeLoading] = useState(false);
  const [showPayeeDropdown, setShowPayeeDropdown] = useState(false);
  const isEditing = !!initial;

  // Search payees when typing
  useEffect(() => {
    if (!payeeSearch || payeeSearch.length < 1 || isEditing) return;
    const timer = setTimeout(async () => {
      setPayeeLoading(true);
      try {
        const res = await payoutAPI.payees({ search: payeeSearch, payee_type: form.payee_type !== 'all' ? form.payee_type : undefined });
        setPayeeResults(res.data || []);
        setShowPayeeDropdown(true);
      } catch { setPayeeResults([]); }
      setPayeeLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [payeeSearch, form.payee_type, isEditing]);

  const selectPayee = (p) => {
    setForm(f => ({
      ...f,
      payee_type: p.payee_type,
      payee_name: p.payee_name,
      payee_id: p.payee_id,
      agency_name: p.agency_name || '',
      agent_name: p.payee_type === 'Agent' ? p.payee_name : '',
    }));
    setPayeeSearch(p.payee_name);
    setShowPayeeDropdown(false);
  };

  const clearPayee = () => {
    setForm(f => ({ ...f, payee_name: '', payee_id: '', agency_name: '', agent_name: '' }));
    setPayeeSearch('');
    setShowPayeeDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.payee_name || !form.amount) {
      toast.error('Payee and Amount are required');
      return;
    }
    if (!form.payment_date) {
      toast.error('Payment Date is required');
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...form, amount: parseFloat(form.amount) });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Payee Selection */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">Payee Type *</Label>
          <Select value={form.payee_type} onValueChange={v => { set('payee_type', v); clearPayee(); }} disabled={isEditing}>
            <SelectTrigger data-testid="ledger-payee-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAYEE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Label className="text-xs font-medium">Payee *</Label>
          {isEditing ? (
            <Input value={form.payee_name} disabled className="bg-slate-50" />
          ) : (
            <>
              <div className="relative">
                <Input
                  value={payeeSearch}
                  onChange={e => setPayeeSearch(e.target.value)}
                  placeholder={`Search ${form.payee_type.toLowerCase()}...`}
                  data-testid="ledger-payee-search"
                />
                {form.payee_id && (
                  <button type="button" onClick={clearPayee} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                )}
              </div>
              {showPayeeDropdown && payeeResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                  {payeeResults.map(p => (
                    <button
                      key={p.payee_id}
                      type="button"
                      onClick={() => selectPayee(p)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b last:border-0"
                    >
                      <div className="font-medium">{p.payee_name}</div>
                      <div className="text-xs text-slate-500">{p.payee_type} {p.agency_name ? `• ${p.agency_name}` : ''}</div>
                    </button>
                  ))}
                </div>
              )}
              {payeeLoading && <div className="absolute right-8 top-1/2 -translate-y-1/2"><Loader2 size={14} className="animate-spin text-slate-400" /></div>}
            </>
          )}
        </div>
      </div>

      {/* Entry Details */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">Entry Type *</Label>
          <Select value={form.entry_type} onValueChange={v => set('entry_type', v)}>
            <SelectTrigger data-testid="ledger-entry-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ENTRY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium">Amount *</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
            <Input
              type="number"
              step="0.01"
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              className="pl-7"
              placeholder="0.00"
              data-testid="ledger-amount"
            />
          </div>
        </div>
      </div>

      {/* Payment Info - Key Simplified Fields */}
      <div className="p-3 bg-slate-50 rounded-lg border">
        <h4 className="text-xs font-semibold text-slate-600 mb-3 flex items-center gap-2">
          <CreditCard size={14} /> Payment Information
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium">Payment Date *</Label>
            <Input
              type="date"
              value={form.payment_date}
              onChange={e => set('payment_date', e.target.value)}
              data-testid="ledger-payment-date"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">Status</Label>
            <Select value={form.payment_status} onValueChange={v => set('payment_status', v)}>
              <SelectTrigger data-testid="ledger-payment-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium">Payment Method</Label>
            <Select value={form.payment_method} onValueChange={v => set('payment_method', v)}>
              <SelectTrigger data-testid="ledger-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium">Reference #</Label>
            <Input
              value={form.reference_number}
              onChange={e => set('reference_number', e.target.value)}
              placeholder="Check # or Transaction ID"
              data-testid="ledger-reference"
            />
          </div>
        </div>
      </div>

      {/* Optional Details */}
      <details className="group">
        <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700">
          + Additional Details (optional)
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Client Name</Label>
            <Input value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder="Client name" />
          </div>
          <div>
            <Label className="text-xs">Product</Label>
            <Input value={form.product} onChange={e => set('product', e.target.value)} placeholder="Product name" />
          </div>
          <div>
            <Label className="text-xs">Carrier</Label>
            <Input value={form.carrier} onChange={e => set('carrier', e.target.value)} placeholder="Carrier name" />
          </div>
          <div>
            <Label className="text-xs">Effective Date</Label>
            <Input type="date" value={form.effective_date} onChange={e => set('effective_date', e.target.value)} />
          </div>
        </div>
      </details>

      {/* Notes */}
      <div>
        <Label className="text-xs font-medium">Notes</Label>
        <Textarea
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="Additional notes..."
          rows={2}
          data-testid="ledger-notes"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="ledger-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
          {isEditing ? 'Update Entry' : (form.payment_status === 'Paid' ? 'Save & Mark Paid' : 'Save Entry')}
        </Button>
      </DialogFooter>
    </form>
  );
}

const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '$0.00';
  return n < 0 ? `-$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

export default function LedgerPage() {
  const { user } = useAuth();
  const canManage = isKRRole(user);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({ payment_status: 'all', entry_type: 'all', payee_type: 'all' });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({});

  // Dialog states
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  // Selection for bulk actions
  const [selected, setSelected] = useState(new Set());
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [bulkPayForm, setBulkPayForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Direct Deposit',
    reference_number: '',
  });
  const [bulkPaying, setBulkPaying] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize, search: debouncedSearch || undefined,
        status: filters.payment_status !== 'all' ? filters.payment_status : undefined,
        entry_type: filters.entry_type !== 'all' ? filters.entry_type : undefined,
        payee_type: filters.payee_type !== 'all' ? filters.payee_type : undefined,
      };
      const [res, statsRes] = await Promise.all([
        payoutAPI.ledger.list(params),
        payoutAPI.ledger.stats()
      ]);
      setItems(res.data.items);
      setTotal(res.data.total);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load entries');
    }
    setLoading(false);
  }, [page, pageSize, debouncedSearch, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (data) => {
    if (editItem) {
      await payoutAPI.ledger.update(editItem.ledger_id, data);
      toast.success('Entry updated');
    } else {
      await payoutAPI.ledger.create(data);
      toast.success(data.payment_status === 'Paid' ? 'Entry saved & marked as Paid' : 'Entry saved');
    }
    setEditItem(null);
    loadData();
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete this ${item.entry_type} entry for ${fmtMoney(item.amount)}?`)) return;
    try {
      await payoutAPI.ledger.delete(item.ledger_id);
      toast.success('Entry deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleMarkPaid = async (item) => {
    try {
      await payoutAPI.ledger.update(item.ledger_id, { payment_status: 'Paid', payment_date: item.payment_date || new Date().toISOString().split('T')[0] });
      toast.success('Marked as Paid & statement generated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const toggleSelect = (id) => {
    const newSet = new Set(selected);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelected(newSet);
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.ledger_id)));
    }
  };

  const handleBulkPay = async () => {
    if (selected.size === 0) return;
    setBulkPaying(true);
    try {
      await payoutAPI.ledger.markPaid({
        ledger_ids: Array.from(selected),
        payment_date: bulkPayForm.payment_date,
        payment_method: bulkPayForm.payment_method,
        reference_number: bulkPayForm.reference_number,
      });
      toast.success(`${selected.size} entries marked as Paid`);
      setSelected(new Set());
      setBulkPayOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    }
    setBulkPaying(false);
  };

  const scheduledItems = items.filter(i => i.payment_status !== 'Paid');
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-6 space-y-6" data-testid="ledger-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payout Entries</h1>
          <p className="text-sm text-slate-500">Add entries, assign payees, and mark as paid</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-btn">
              <Upload size={16} className="mr-2" /> Import
            </Button>
            <Button onClick={() => { setEditItem(null); setFormOpen(true); }} data-testid="add-entry-btn">
              <Plus size={16} className="mr-2" /> Add Entry
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg"><Clock size={20} className="text-amber-600" /></div>
              <div>
                <p className="text-xs text-amber-600 font-medium">Scheduled</p>
                <p className="text-lg font-bold text-amber-700">{fmtMoney(stats.by_status?.Scheduled?.total || 0)}</p>
                <p className="text-xs text-amber-500">{stats.by_status?.Scheduled?.count || 0} entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg"><CheckCircle2 size={20} className="text-emerald-600" /></div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Paid</p>
                <p className="text-lg font-bold text-emerald-700">{fmtMoney(stats.by_status?.Paid?.total || 0)}</p>
                <p className="text-xs text-emerald-500">{stats.by_status?.Paid?.count || 0} entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg"><FileSpreadsheet size={20} className="text-slate-600" /></div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Entries</p>
                <p className="text-lg font-bold text-slate-700">{stats.total_entries || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg"><DollarSign size={20} className="text-slate-600" /></div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Amount</p>
                <p className="text-lg font-bold text-slate-700">
                  {fmtMoney((stats.by_status?.Scheduled?.total || 0) + (stats.by_status?.Paid?.total || 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-sky-50 rounded-lg border border-sky-200">
          <span className="text-sm font-medium text-sky-700">{selected.size} selected</span>
          <Button size="sm" onClick={() => setBulkPayOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle2 size={14} className="mr-1" /> Mark as Paid
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="pl-9 w-64"
                  data-testid="ledger-search"
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                <Filter size={14} className="mr-1" /> Filters
              </Button>
            </div>
            <span className="text-xs text-slate-500">{total} entries</span>
          </div>
          {showFilters && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
              <Select value={filters.payment_status} onValueChange={v => setFilters(f => ({ ...f, payment_status: v }))}>
                <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.entry_type} onValueChange={v => setFilters(f => ({ ...f, entry_type: v }))}>
                <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {ENTRY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.payee_type} onValueChange={v => setFilters(f => ({ ...f, payee_type: v }))}>
                <SelectTrigger className="w-28 h-8 text-xs"><SelectValue placeholder="Payee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payees</SelectItem>
                  {PAYEE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <DollarSign size={32} className="mx-auto mb-2 opacity-50" />
              <p className="font-medium">No entries found</p>
              <p className="text-sm">Add your first payout entry to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y">
                  <tr>
                    {canManage && (
                      <th className="px-4 py-3 text-left w-10">
                        <Checkbox
                          checked={selected.size === items.length && items.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Payee</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Payment Date</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Method</th>
                    {canManage && <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(item => (
                    <tr key={item.ledger_id} className="hover:bg-slate-50" data-testid={`ledger-row-${item.ledger_id}`}>
                      {canManage && (
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={selected.has(item.ledger_id)}
                            onCheckedChange={() => toggleSelect(item.ledger_id)}
                            disabled={item.payment_status === 'Paid'}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{item.payee_name}</div>
                        <div className="text-xs text-slate-500">{item.payee_type}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">{item.entry_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        <span className={item.amount < 0 ? 'text-red-600' : 'text-slate-900'}>
                          {fmtMoney(item.amount)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{item.payment_date || '-'}</td>
                      <td className="px-4 py-3">
                        <Badge className={`${STATUS_COLORS[item.payment_status] || STATUS_COLORS.Scheduled} flex items-center gap-1 w-fit`}>
                          {STATUS_ICONS[item.payment_status] || STATUS_ICONS.Scheduled}
                          {item.payment_status || 'Scheduled'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{item.payment_method || '-'}</td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {item.payment_status !== 'Paid' && (
                              <Button size="sm" variant="ghost" className="h-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => handleMarkPaid(item)}>
                                <CheckCircle2 size={14} className="mr-1" /> Pay
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditItem(item); setFormOpen(true); }}>
                              <Pencil size={14} />
                            </Button>
                            {item.payment_status !== 'Paid' && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={() => handleDelete(item)}>
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft size={14} />
                </Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={v => { setFormOpen(v); if (!v) setEditItem(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Entry' : 'Add Payout Entry'}</DialogTitle>
            <DialogDescription>
              {editItem ? 'Update the entry details' : 'Create a new payout entry and assign payment info'}
            </DialogDescription>
          </DialogHeader>
          <LedgerEntryForm
            initial={editItem}
            onSave={handleSave}
            onClose={() => { setFormOpen(false); setEditItem(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk Pay Dialog */}
      <Dialog open={bulkPayOpen} onOpenChange={setBulkPayOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark {selected.size} Entries as Paid</DialogTitle>
            <DialogDescription>
              This will mark the selected entries as paid and auto-generate statements for each payee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium">Payment Date *</Label>
              <Input
                type="date"
                value={bulkPayForm.payment_date}
                onChange={e => setBulkPayForm(f => ({ ...f, payment_date: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Payment Method</Label>
              <Select value={bulkPayForm.payment_method} onValueChange={v => setBulkPayForm(f => ({ ...f, payment_method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Reference # (optional)</Label>
              <Input
                value={bulkPayForm.reference_number}
                onChange={e => setBulkPayForm(f => ({ ...f, reference_number: e.target.value }))}
                placeholder="Check # or batch reference"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPayOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkPay} disabled={bulkPaying} className="bg-emerald-600 hover:bg-emerald-700">
              {bulkPaying ? <Loader2 size={16} className="animate-spin mr-2" /> : <CheckCircle2 size={16} className="mr-2" />}
              Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog - placeholder */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Entries</DialogTitle>
            <DialogDescription>Upload a spreadsheet to import multiple entries</DialogDescription>
          </DialogHeader>
          <div className="py-8 text-center text-slate-500">
            <Upload size={32} className="mx-auto mb-2 opacity-50" />
            <p>Import functionality coming soon</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
